import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { apiUrl } from '../utils/api';

function getModelGroup(modelId: string): string {
  if (modelId.startsWith('gpt-5')) return 'GPT-5';
  if (modelId.startsWith('gpt-4o')) return 'GPT-4o';
  if (modelId.startsWith('gpt-4')) return 'GPT-4';
  if (modelId.startsWith('gpt-3')) return 'GPT-3.5';
  if (modelId.startsWith('gpt-realtime')) return 'Realtime';
  if (modelId.startsWith('gpt-audio')) return 'Audio';
  if (modelId.startsWith('gpt-image')) return 'Image';
  return 'Other';
}

const GROUP_ORDER = ['GPT-5', 'GPT-4o', 'GPT-4', 'GPT-3.5', 'Realtime', 'Audio', 'Image', 'Other'];

interface AISettingsProps {
  projectId: number | null;
  access?: string;
  isGlobalAdmin?: boolean;
}

interface AIModel {
  id: string;
  name: string;
}

interface AISettingsState {
  use_rag: boolean;
  use_db_tools: boolean;
  show_thoughts: boolean;
  enable_thinking: boolean;
  thinking_level: 'low' | 'medium' | 'high';
}

interface ProjectInfo {
  project_id: number;
  project_name: string;
  project_number: string;
}

interface RAGCollection {
  id: string;
  description: string;
  files: number;
  chunks: number;
}

const AISettings: React.FC<AISettingsProps> = ({ projectId, access = 'none', isGlobalAdmin = false }) => {
  const readOnly = access !== 'write';

  // Model selection state (Global Admin only)
  const [currentModel, setCurrentModel] = useState<string>('gpt-4o-mini');
  const [availableModels, setAvailableModels] = useState<AIModel[]>([]);
  const [loadingModels, setLoadingModels] = useState<boolean>(false);
  const [savingModel, setSavingModel] = useState<boolean>(false);
  const [modelSaved, setModelSaved] = useState<boolean>(false);

  // COMMENTED OUT - Winston specific settings (now using OpenAI)
  // AI settings state (from original AISettings)
  // const [settings, setSettings] = useState<AISettingsState>({
  //   use_rag: true,
  //   use_db_tools: true,
  //   show_thoughts: true,  // Enable by default to show DB agent thoughts
  //   enable_thinking: true,  // Enable by default for DB agent thinking
  //   thinking_level: 'medium'
  // });

  // COMMENTED OUT - Winston system prompt settings
  // System prompt settings (from WinstonSettings)
  // const [systemFiles, setSystemFiles] = useState<string[]>([]);
  // const [currentSystem, setCurrentSystem] = useState<string | null>(null);
  // const [selected, setSelected] = useState<string>('');
  // const [savingSystem, setSavingSystem] = useState<boolean>(false);

  // RAG collections (from WinstonSettings)
  const [collections, setCollections] = useState<RAGCollection[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [descriptionText, setDescriptionText] = useState<string>('');

  const [projectInfo, setProjectInfo] = useState<ProjectInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  // const [saving, setSaving] = useState<boolean>(false); // COMMENTED OUT - Winston specific
  const [error, setError] = useState<string>('');

  // COMMENTED OUT - Winston specific (now using OpenAI)
  // Load available system files once
  // useEffect(() => {
  //   fetch(apiUrl('/api/winston/system-files'))
  //     .then(r => r.json())
  //     .then(d => setSystemFiles(d.files || []))
  //     .catch(() => setSystemFiles([]));
  // }, []);

  // Load AI model config (for all users to see current model, Global Admin can change)
  useEffect(() => {
    setLoadingModels(true);
    fetch(apiUrl('/api/ai/config'), { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setCurrentModel(d.model);
          setAvailableModels(d.available_models || []);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingModels(false));
  }, []);

  const handleModelSave = async (newModel: string) => {
    setSavingModel(true);
    setModelSaved(false);
    try {
      const r = await fetch(apiUrl('/api/ai/config'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ model: newModel })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to save');
      setCurrentModel(data.model);
      setModelSaved(true);
      setTimeout(() => setModelSaved(false), 3000);
    } catch (err: any) {
      alert(err.message || 'Failed to save model');
    } finally {
      setSavingModel(false);
    }
  };

  // Load project info and RAG collections (OpenAI)
  useEffect(() => {
    if (!projectId) {
      setProjectInfo(null);
      setCollections([]);
      return;
    }

    setLoading(true);
    setError('');

    // Load project basic info
    fetch(apiUrl(`/api/projects/${projectId}`), {
      credentials: 'include'  // Session cookie for authentication
    })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to load project');
        }
        setProjectInfo({
          project_id: data.project_id,
          project_name: data.project_name,
          project_number: data.project_number
        });
      })
      .catch(err => {
        console.error('Error loading project info:', err);
        setError(err.message || 'Failed to load project information');
      })
      .finally(() => {
        setLoading(false);
      });

    // Load RAG collections (using OpenAI embeddings)
    fetch(apiUrl(`/api/rag/collections?project_id=${projectId}`), {
      credentials: 'include'  // Session cookie for authentication
    })
      .then(r => r.json())
      .then(d => setCollections(Array.isArray(d?.collections) ? d.collections : []))
      .catch(() => setCollections([]));

    // COMMENTED OUT - Winston specific AI settings
    // Load AI settings
    // fetch(apiUrl(`/api/projects/${projectId}/ai-settings`))
    //   .then(async response => {
    //     const data = await response.json();
    //     if (!response.ok) {
    //       throw new Error(data.error || 'Failed to load settings');
    //     }
    //     setProjectInfo({
    //       project_id: data.project_id,
    //       project_name: data.project_name,
    //       project_number: data.project_number
    //     });
    //     setSettings(data.settings);
    //   })
    //   .catch(err => {
    //     console.error('Error loading AI settings:', err);
    //     setError(err.message || 'Failed to load AI settings');
    //   });

    // COMMENTED OUT - Winston system prompt
    // Load system prompt selection
    // fetch(apiUrl(`/api/winston/project-system?project_id=${projectId}`))
    //   .then(r => r.json())
    //   .then(d => {
    //     const sf = d.system_file || '';
    //     setCurrentSystem(sf);
    //     setSelected(sf);
    //   })
    //   .catch(() => {
    //     setCurrentSystem('');
    //     setSelected('');
    //   });
  }, [projectId]);

  // COMMENTED OUT - Winston specific settings handler
  // const handleSettingChange = (key: keyof AISettingsState, value: boolean | string) => {
  //   setSettings(prev => ({
  //     ...prev,
  //     [key]: value
  //   }));
  // };

  // COMMENTED OUT - Winston AI settings save
  // const saveSettings = async () => {
  //   if (readOnly || !projectId || saving) return;
  //
  //   setSaving(true);
  //   setError('');
  //
  //   try {
  //     const response = await fetch(apiUrl(`/api/projects/${projectId}/ai-settings`), {
  //       method: 'PUT',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify(settings)
  //     });
  //
  //     const data = await response.json();
  //     if (!response.ok) {
  //       throw new Error(data.error || 'Failed to save settings');
  //     }
  //
  //     // Update local state with saved settings
  //     setSettings(data.settings);
  //     alert('AI settings saved successfully!');
  //
  //   } catch (err: any) {
  //     console.error('Error saving AI settings:', err);
  //     setError(err.message || 'Failed to save settings');
  //     alert('Error saving settings: ' + (err.message || 'Unknown error'));
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  // COMMENTED OUT - Winston system prompt save
  // System prompt save function
  // const saveSystemPrompt = async () => {
  //   if (readOnly || !projectId) return;
  //   setSavingSystem(true);
  //   try {
  //     const r = await fetch(apiUrl('/api/winston/project-system'), {
  //       method: 'PUT',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ project_id: projectId, system_file: selected || null })
  //     });
  //     const data = await r.json();
  //     if (!r.ok) throw new Error(data.error || 'Save failed');
  //     setCurrentSystem(selected || '');
  //     alert('System prompt saved successfully!');
  //   } catch (e: any) {
  //     alert(e.message || 'Save failed');
  //   } finally {
  //     setSavingSystem(false);
  //   }
  // };

  // RAG upload functions
  const handleFolderPick = () => {
    try { fileInputRef.current?.click(); } catch {}
  };

  const onFolderSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !projectId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('project_id', String(projectId));
      fd.append('description', descriptionText);
      for (const f of files) fd.append('files', f);
      const r = await fetch(apiUrl('/api/rag/upload'), {
        method: 'POST',
        body: fd,
        credentials: 'include'  // Session cookie for authentication
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Upload failed');
      // refresh collections
      const rr = await fetch(apiUrl(`/api/rag/collections?project_id=${projectId}`), {
        credentials: 'include'
      });
      const dd = await rr.json();
      setCollections(Array.isArray(dd?.collections) ? dd.collections : []);
      setDescriptionText('');
      e.target.value = '';
      alert(`Uploaded: ${data.files} files, chunks: ${data.chunks}`);
    } catch (err: any) {
      alert(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (access === 'none') {
    return (
      <div style={{ padding: 16 }}>
        <h2>AI Settings</h2>
        <div style={{ color: '#a00', marginTop: 8 }}>
          Access denied. You don't have permission to view AI settings.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 800 }}>

      {!projectId && (
        <div style={{ 
          padding: 16, 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: 6, 
          marginBottom: 16,
          color: '#856404'
        }}>
          Select a project in the left sidebar to manage its AI settings.
        </div>
      )}

      {projectInfo && (
        <div style={{ 
          padding: 12, 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #dee2e6', 
          borderRadius: 6, 
          marginBottom: 16 
        }}>
          <strong>Project:</strong> {projectInfo.project_name} 
          {projectInfo.project_number && ` (${projectInfo.project_number})`}
        </div>
      )}

      {error && (
        <div style={{ 
          padding: 12, 
          backgroundColor: '#f8d7da', 
          border: '1px solid #f5c6cb', 
          borderRadius: 6, 
          marginBottom: 16,
          color: '#721c24'
        }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{ padding: 16, textAlign: 'center', color: '#6c757d' }}>
          Loading AI settings...
        </div>
      )}

      {projectId && !loading && (
        <div>
          {/* COMMENTED OUT - Winston System Prompt Section
          <div style={{ marginBottom: 32 }}>
            <h3 style={{ marginBottom: 16, color: '#495057' }}>System Prompt</h3>
            ... (commented out for now - Winston specific)
          </div>
          */}

          {/* COMMENTED OUT - Winston Agent Capabilities Section
          <div style={{ marginBottom: 32 }}>
            <h3 style={{ marginBottom: 16, color: '#495057' }}>Agent Capabilities</h3>
            ... (commented out for now - Winston specific)
          </div>
          */}

          {/* COMMENTED OUT - Winston Debugging & Transparency Section
          <div style={{ marginBottom: 32 }}>
            <h3 style={{ marginBottom: 16, color: '#495057' }}>Debugging & Transparency</h3>
            ... (commented out for now - Winston specific)
          </div>
          */}

          {/* AI Model Section */}
          <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#e7f3ff', border: '1px solid #bee5eb', borderRadius: 6 }}>
            <strong>AI Model</strong>
            {isGlobalAdmin ? (
              <div style={{ marginTop: 12 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: '0.9em', color: '#0c5460' }}>
                  Select Model (applies to all users):
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Autocomplete
                    value={availableModels.find(m => m.id === currentModel) || null}
                    onChange={(_, newVal) => {
                      if (newVal) {
                        setCurrentModel(newVal.id);
                        setModelSaved(false);
                      }
                    }}
                    loading={loadingModels}
                    loadingText="Fetching models from OpenAI..."
                    options={[...availableModels].sort((a, b) => {
                      const ga = GROUP_ORDER.indexOf(getModelGroup(a.id));
                      const gb = GROUP_ORDER.indexOf(getModelGroup(b.id));
                      return ga !== gb ? ga - gb : b.id.localeCompare(a.id);
                    })}
                    groupBy={(option) => getModelGroup(option.id)}
                    getOptionLabel={(option) => option.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder={loadingModels ? 'Loading models...' : 'Search model...'}
                        sx={{ backgroundColor: 'white', minWidth: 280 }}
                      />
                    )}
                    sx={{ minWidth: 280 }}
                  />
                  <button
                    onClick={() => handleModelSave(currentModel)}
                    disabled={savingModel}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: savingModel ? '#6c757d' : '#0c5460',
                      color: 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: savingModel ? 'not-allowed' : 'pointer',
                      fontSize: '0.9em'
                    }}
                  >
                    {savingModel ? 'Saving...' : 'Save Model'}
                  </button>
                  {modelSaved && (
                    <span style={{ color: '#155724', fontSize: '0.9em', fontWeight: 600 }}>
                      Saved successfully!
                    </span>
                  )}
                </div>
                <p style={{ margin: '8px 0 0 0', fontSize: '0.85em', color: '#0c5460' }}>
                  The selected model will be used for all users across the application.
                </p>
              </div>
            ) : (
              <p style={{ margin: '8px 0 0 0', fontSize: '0.9em', color: '#0c5460' }}>
                Currently using: <strong>{availableModels.find(m => m.id === currentModel)?.name || currentModel}</strong>
              </p>
            )}
          </div>

          {/* Project RAG Section */}
          <div style={{ marginBottom: 32 }}>
            <h3 style={{ marginBottom: 16, color: '#495057' }}>Project Knowledge Base (RAG)</h3>
            <p style={{ fontSize: '0.9em', color: '#6c757d', marginBottom: 16 }}>
              Upload project documents (PDF, Word, text, Excel, CSV) to create a searchable knowledge base for this project.
            </p>
            
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
              <input
                type="text"
                placeholder="Collection description (e.g. Project Specs, Contracts, etc.)"
                value={descriptionText}
                onChange={e => setDescriptionText(e.target.value)}
                style={{
                  flex: 1,
                  padding: 8,
                  border: '1px solid #ced4da',
                  borderRadius: 4,
                  backgroundColor: readOnly ? '#e9ecef' : 'white'
                }}
                disabled={readOnly || !projectId || uploading}
              />
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                multiple
                accept=".pdf,.doc,.docx,.txt,.md,.xlsx,.xls,.csv"
                onChange={onFolderSelected}
              />
              <button
                onClick={handleFolderPick}
                disabled={readOnly || !projectId || uploading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: readOnly || uploading ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: readOnly || uploading ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {uploading ? 'Uploading...' : 'Upload Files'}
              </button>
            </div>
            
            <div style={{ 
              border: '1px solid #dee2e6', 
              borderRadius: 6, 
              padding: 16,
              backgroundColor: '#f8f9fa'
            }}>
              <h4 style={{ marginBottom: 12, color: '#495057' }}>Uploaded Collections</h4>
              {(!Array.isArray(collections) || collections.length === 0) && 
                <div style={{ color: '#6c757d', fontStyle: 'italic' }}>No collections uploaded yet.</div>
              }
              {Array.isArray(collections) && collections.map(c => (
                <div key={c.id} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  padding: '12px 0', 
                  borderBottom: collections.indexOf(c) < collections.length - 1 ? '1px solid #dee2e6' : 'none'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {c.description || '(no description)'}
                    </div>
                    <div style={{ fontSize: '0.85em', color: '#6c757d' }}>
                      Files: {c.files || 0} · Chunks: {c.chunks || 0}
                    </div>
                  </div>
                  {!readOnly && (
                    <button
                      onClick={async () => {
                        if (!window.confirm('Delete this collection and its embeddings?')) return;
                        try {
                          const r = await fetch(apiUrl(`/api/rag/collections/${c.id}?project_id=${projectId}`), {
                            method: 'DELETE',
                            credentials: 'include'
                          });
                          const data = await r.json();
                          if (!r.ok) throw new Error(data.error || 'Delete failed');
                          const rr = await fetch(apiUrl(`/api/rag/collections?project_id=${projectId}`), {
                            credentials: 'include'
                          });
                          const dd = await rr.json();
                          setCollections(Array.isArray(dd?.collections) ? dd.collections : []);
                          alert('Collection deleted successfully');
                        } catch (err) {
                          alert('Error deleting collection: ' + err.message);
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: '0.85em'
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* COMMENTED OUT - Winston Save Settings Section
          <div style={{ paddingTop: 24, borderTop: '2px solid #dee2e6' }}>
            <button onClick={saveSettings}...>Save Agent Settings</button>
          </div>
          */}

          <div style={{
            marginTop: 24,
            padding: 16,
            backgroundColor: '#f0f7ff',
            border: '1px solid #e3f2fd',
            borderRadius: 6,
            fontSize: '0.9em',
            color: '#1976d2'
          }}>
            <strong>💡 How it works:</strong> When you upload documents, the AI assistant uses OpenAI embeddings to create a searchable knowledge base.
            Users can then ask questions about the uploaded documents and get accurate answers based on the content.
          </div>
        </div>
      )}
    </div>
  );
};

export default AISettings;
